import {FederatedPointerEvent, Graphics} from "pixi.js";
import {BlockItemDrawProps} from "../FrameBlockItem";
import {lineBlueLight} from "../../../const";

// fill the face: its polygon when a sloped stick cuts into it, else its rect.
export function drawFace(g: Graphics, props: BlockItemDrawProps, left: number, top: number, w: number, h: number) {
    if(props.polygon) g.drawPolygon(props.polygon.flatMap(p => [p.x, p.y]));
    else g.drawRect(left, top, w, h);
}

// a cell face that fills its rect (drawFill) and is clickable: tapping it selects
// the cell, which drives the contextual "Selected Lite/Panel" ribbon tab (where
// the convert / split / remove actions live). The face keeps its own highlight in
// sync with the canvas selection.
export abstract class ClickableFace {
    drawFill: (props: BlockItemDrawProps) => void

    private wiredTo: Graphics | null = null;
    private tapHandler: ((e: FederatedPointerEvent) => void) | null = null;

    // true while this cell is the canvas selection — draws a hover-like wash over
    // the face so it's clear which cell the contextual tab acts on. lastProps lets
    // us repaint on selection change without a full grid relayout.
    private selected = false;
    private lastProps: BlockItemDrawProps | null = null;
    private destroyed = false;

    // subscription that keeps `selected` in sync with the canvas selection; wired
    // alongside the pointer handler (once the graphics exists) and cancelled on
    // unwire/destroy.
    private selectionSub: (() => void) | null = null;

    constructor(fill: (props: BlockItemDrawProps) => void) {
        this.drawFill = fill;
    }

    draw(props: BlockItemDrawProps) {
        this.lastProps = props;
        this.drawFill(props);
        this.drawHighlight(props);
        this.wire(props);
    }

    // translucent wash + accent border drawn on top of the face fill while
    // selected. No-op otherwise. drawFill clears the graphics each pass, so this
    // must run after it.
    private drawHighlight(props: BlockItemDrawProps) {
        if(!this.selected) return;

        const g = props.graphics;
        const left = Math.min(props.startX, props.endX);
        const right = Math.max(props.startX, props.endX);
        const top = Math.min(props.startY, props.endY);
        const bottom = Math.max(props.startY, props.endY);

        const w = right - left;
        const h = bottom - top;
        if(w <= 0 || h <= 0) return;

        g.lineStyle(2, lineBlueLight, 1);
        g.beginFill(lineBlueLight, 0.15);
        drawFace(g, props, left, top, w, h);
        g.endFill();
    }

    private setSelected(value: boolean) {
        if(this.selected === value) return;
        this.selected = value;

        // an action that closes the menu may rebuild the grid first, destroying
        // this face; its shared graphics now belongs to a replacement, so don't
        // repaint over it.
        if(this.destroyed) return;

        // repaint in place (fill + highlight); skip if we've never drawn yet.
        if(this.lastProps) {
            this.drawFill(this.lastProps);
            this.drawHighlight(this.lastProps);
        }
    }

    // wire pointer interactivity onto the item's graphics once. The graphics is
    // stable across redraws (the FrameBlockItem's `lines`) and clearing it each
    // draw doesn't drop listeners, so we only attach the first time.
    private wire(props: BlockItemDrawProps) {
        const g = props.graphics;
        if(this.wiredTo === g) return;
        this.unwire();

        g.eventMode = "static";
        g.cursor = "pointer";
        const handler = (e: FederatedPointerEvent) => {
            e.stopPropagation();
            this.select(e.ctrlKey || e.metaKey);
        };
        g.on("pointertap", handler);

        this.wiredTo = g;
        this.tapHandler = handler;

        // keep our highlight in sync with the canvas selection (so it clears when
        // another element is selected). block id is stable for this face instance.
        this.selectionSub = props.elev.viewSettings.selection.subscribeAndFireLast(() => {
            this.setSelected(props.elev.viewSettings.isSelected(props.block.id));
        });
    }

    // select this cell on the canvas: emit the selection (with the cell's resolved
    // size, so a later split can divide the existing footprint) and switch to its
    // contextual ribbon tab. ctrl/meta-click adds to the current cell selection.
    // A void or a door slot selects alone.
    private select(additive: boolean) {
        const props = this.lastProps;
        if(!props) return;

        const vs = props.elev.viewSettings;

        // a cell is not a valid answer to a stick pick (e.g. Split By): cancel it.
        if(vs.stickPick.value) {
            vs.stickPick.emit(null);
            return;
        }

        const block = props.block;
        if(block.kind === "void") {
            vs.selectItem({type: "void", id: block.id}, false);
            vs.activeTab.emit("selected");
            return;
        }

        if(block.kind === "doors") {
            vs.selectItem({type: "door-slot", id: block.id}, false);
            vs.activeTab.emit("selected");
            return;
        }

        if(block.kind !== "lite" && block.kind !== "fixedPanel") return;

        vs.selectItem({
            type: block.kind === "fixedPanel" ? "panel" : "lite",
            id: block.id,
            size: {
                width: Math.abs(props.endX - props.startX),
                height: Math.abs(props.endY - props.startY),
            },
        }, additive);
        vs.activeTab.emit("selected");
    }

    private unwire() {
        if(this.wiredTo && this.tapHandler) {
            this.wiredTo.off("pointertap", this.tapHandler);
        }
        this.selectionSub?.();
        this.selectionSub = null;
        this.wiredTo = null;
        this.tapHandler = null;
    }

    destroy() {
        this.destroyed = true;
        this.unwire();
    }
}
